import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import FloatingDock from '@/components/layout/FloatingDock';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  Trophy, Award, Crown,
  Palette, Laugh, Newspaper, Mic, Sparkles, BookOpen,
  ChevronRight, TrendingUp, Users, BarChart3, ArrowLeft,
  Flame, SmilePlus, Skull, AlertTriangle
} from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import RoastGalleryTab from '@/components/awards/RoastGalleryTab';
import MemeGalleryTab from '@/components/awards/MemeGalleryTab';
import HallOfShameTab from '@/components/awards/HallOfShameTab';
import BSAwardTab from '@/components/awards/BSAwardTab';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Award category configurations
const CATEGORY_CONFIG = {
  artistic: { icon: Palette, color: "#8b5cf6", emoji: "🎨" },
  comedic: { icon: Laugh, color: "#f59e0b", emoji: "😂" },
  journalistic: { icon: Newspaper, color: "#3b82f6", emoji: "📰" },
  podcast: { icon: Mic, color: "#ef4444", emoji: "🎙️" },
  inspirational: { icon: Sparkles, color: "#10b981", emoji: "✨" },
  educational: { icon: BookOpen, color: "#06b6d4", emoji: "📚" },
  roastgallery: { icon: Flame, color: "#dc2626", emoji: "🔥" }
};

const Awards = ({ user }) => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [leaderboards, setLeaderboards] = useState({});
  const [winners, setWinners] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [period, setPeriod] = useState('month');
  
  // RoastGallery state
  const [roastLeaderboard, setRoastLeaderboard] = useState([]);
  const [roastFeed, setRoastFeed] = useState([]);
  const [topRoaster, setTopRoaster] = useState(null);
  const [roastForm, setRoastForm] = useState({ target_post_id: '', content: '' });
  const [submittingRoast, setSubmittingRoast] = useState(false);
  const [activeTab, setActiveTab] = useState('awards');

  // MemeGallery state
  const [memeLeaderboard, setMemeLeaderboard] = useState([]);
  const [memeFeed, setMemeFeed] = useState([]);
  const [topMemer, setTopMemer] = useState(null);
  const [memeSort, setMemeSort] = useState('recent');
  const [memeOfTheDay, setMemeOfTheDay] = useState(null);

  // HallofShame state
  const [shameLeaderboard, setShameLeaderboard] = useState([]);
  const [shameFeed, setShameFeed] = useState([]);
  const [topShamer, setTopShamer] = useState(null);
  const [shameSort, setShameSort] = useState('recent');

  // BSAward state
  const [bsLeaderboard, setBsLeaderboard] = useState([]);
  const [bsFeed, setBsFeed] = useState([]);
  const [topBsCaller, setTopBsCaller] = useState(null);
  const [bsSort, setBsSort] = useState('recent');

  useEffect(() => {
    fetchData();
    fetchRoastData();
    fetchMemeData();
    fetchShameData();
    fetchBsData();
  }, [period]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [categoriesRes, leaderboardsRes, winnersRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/awards/categories`),
        axios.get(`${API_URL}/awards/leaderboard?period=${period}&limit=5`),
        axios.get(`${API_URL}/awards/winners/${period}`),
        axios.get(`${API_URL}/awards/stats`)
      ]);
      
      setCategories(categoriesRes.data.categories || []);
      setLeaderboards(leaderboardsRes.data.leaderboards || {});
      setWinners(winnersRes.data.winners || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to load awards data');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategoryLeaderboard = async (category) => {
    try {
      const response = await axios.get(
        `${API_URL}/awards/leaderboard/${category}?period=${period}&limit=10`
      );
      setSelectedCategory({
        ...response.data,
        config: CATEGORY_CONFIG[category]
      });
    } catch (error) {
      toast.error('Failed to load leaderboard');
    }
  };

  const fetchRoastData = async () => {
    try {
      const [lbRes, feedRes] = await Promise.all([
        axios.get(`${API_URL}/awards/roastgallery/leaderboard?period=${period}`, { withCredentials: true }),
        axios.get(`${API_URL}/awards/roastgallery/feed?limit=20`, { withCredentials: true })
      ]);
      setRoastLeaderboard(lbRes.data.leaderboard || []);
      setTopRoaster(lbRes.data.top_roaster);
      setRoastFeed(feedRes.data.roasts || []);
    } catch (e) { /* silent */ }
  };

  const submitRoast = async () => {
    if (!roastForm.target_post_id.trim() || !roastForm.content.trim()) {
      toast.error('Post ID and roast content required');
      return;
    }
    setSubmittingRoast(true);
    try {
      await axios.post(`${API_URL}/awards/roastgallery/submit`, {
        target_post_id: roastForm.target_post_id.trim(),
        roast_content: roastForm.content.trim()
      }, { withCredentials: true });
      toast.success('Roast submitted!');
      setRoastForm({ target_post_id: '', content: '' });
      fetchRoastData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit roast');
    } finally {
      setSubmittingRoast(false);
    }
  };

  const fireRoast = async (roastId) => {
    try {
      const res = await axios.post(`${API_URL}/awards/roastgallery/${roastId}/fire`, {}, { withCredentials: true });
      const updateList = (list) => list.map(r => 
        r.roast_id === roastId ? { ...r, fire_count: res.data.fire_count, user_fired: res.data.status === 'fired' } : r
      );
      setRoastLeaderboard(updateList);
      setRoastFeed(updateList);
    } catch (e) {
      toast.error('Failed to fire');
    }
  };

  const fetchMemeData = async () => {
    try {
      const [lbRes, feedRes, motdRes] = await Promise.all([
        axios.get(`${API_URL}/awards/memegallery/leaderboard?period=${period}`, { withCredentials: true }),
        axios.get(`${API_URL}/awards/memegallery/feed?limit=20&sort=${memeSort}`, { withCredentials: true }),
        axios.get(`${API_URL}/awards/memegallery/meme-of-the-day`, { withCredentials: true })
      ]);
      setMemeLeaderboard(lbRes.data.leaderboard || []);
      setTopMemer(lbRes.data.top_memer);
      setMemeFeed(feedRes.data.memes || []);
      setMemeOfTheDay(motdRes.data.meme || null);
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    if (activeTab === 'memegallery') fetchMemeData();
  }, [memeSort]);

  const laughAtMeme = async (postId) => {
    try {
      const res = await axios.post(`${API_URL}/awards/memegallery/${postId}/laugh`, {}, { withCredentials: true });
      const updateList = (list) => list.map(m =>
        m.post_id === postId ? { ...m, laugh_count: res.data.laugh_count, user_laughed: res.data.status === 'laughed' } : m
      );
      setMemeLeaderboard(updateList);
      setMemeFeed(updateList);
      if (memeOfTheDay?.post_id === postId) {
        setMemeOfTheDay(prev => ({ ...prev, laugh_count: res.data.laugh_count, user_laughed: res.data.status === 'laughed' }));
      }
    } catch (e) {
      toast.error('Failed to react');
    }
  };

  const renderMedia = (post) => {
    if (post.image_url) {
      const src = post.image_url.startsWith('http') ? post.image_url : `${process.env.REACT_APP_BACKEND_URL}${post.image_url}`;
      return <img src={src} alt="meme" className="w-full max-h-96 object-contain rounded-xl mt-2" />;
    }
    if (post.video_url) {
      const src = post.video_url.startsWith('http') ? post.video_url : `${process.env.REACT_APP_BACKEND_URL}${post.video_url}`;
      return <video src={src} controls className="w-full max-h-96 rounded-xl mt-2" />;
    }
    if (post.gif_url) {
      return <img src={post.gif_url} alt="gif" className="w-full max-h-96 object-contain rounded-xl mt-2" />;
    }
    if (post.audio_url) {
      const src = post.audio_url.startsWith('http') ? post.audio_url : `${process.env.REACT_APP_BACKEND_URL}${post.audio_url}`;
      return <audio src={src} controls className="w-full mt-2" />;
    }
    return null;
  };

  const fetchShameData = async () => {
    try {
      const [lbRes, feedRes] = await Promise.all([
        axios.get(`${API_URL}/awards/hallofshame/leaderboard?period=${period}`, { withCredentials: true }),
        axios.get(`${API_URL}/awards/hallofshame/feed?limit=20&sort=${shameSort}`, { withCredentials: true })
      ]);
      setShameLeaderboard(lbRes.data.leaderboard || []);
      setTopShamer(lbRes.data.top_shamer);
      setShameFeed(feedRes.data.posts || []);
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    if (activeTab === 'hallofshame') fetchShameData();
  }, [shameSort]);

  const shamePost = async (postId) => {
    try {
      const res = await axios.post(`${API_URL}/awards/hallofshame/${postId}/shame`, {}, { withCredentials: true });
      const updateList = (list) => list.map(p =>
        p.post_id === postId ? { ...p, shame_count: res.data.shame_count, user_shamed: res.data.status === 'shamed' } : p
      );
      setShameLeaderboard(updateList);
      setShameFeed(updateList);
    } catch (e) {
      toast.error('Failed to react');
    }
  };

  const fetchBsData = async () => {
    try {
      const [lbRes, feedRes] = await Promise.all([
        axios.get(`${API_URL}/awards/bsaward/leaderboard?period=${period}`, { withCredentials: true }),
        axios.get(`${API_URL}/awards/bsaward/feed?limit=20&sort=${bsSort}`, { withCredentials: true })
      ]);
      setBsLeaderboard(lbRes.data.leaderboard || []);
      setTopBsCaller(lbRes.data.top_bs_caller);
      setBsFeed(feedRes.data.posts || []);
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    if (activeTab === 'bsaward') fetchBsData();
  }, [bsSort]);

  const callBs = async (postId) => {
    try {
      const res = await axios.post(`${API_URL}/awards/bsaward/${postId}/bs`, {}, { withCredentials: true });
      const updateList = (list) => list.map(p =>
        p.post_id === postId ? { ...p, bs_count: res.data.bs_count, user_bs: res.data.status === 'called' } : p
      );
      setBsLeaderboard(updateList);
      setBsFeed(updateList);
    } catch (e) {
      toast.error('Failed to react');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-white to-amber-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-white/80 dark:bg-gray-900/80 backdrop-blur-lg border-b border-yellow-200 dark:border-yellow-800">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate(-1)} className="p-2 rounded-full hover:bg-gray-100">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center">
                <Trophy className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-heading font-bold text-gray-900 dark:text-white">
                  Socyy Awards
                </h1>
                <p className="text-sm text-gray-500">Celebrating original content creators</p>
              </div>
            </div>
            
            {/* Period Toggle */}
            <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-800 rounded-full p-1">
              <button
                onClick={() => setPeriod('month')}
                className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${
                  period === 'month'
                    ? 'bg-white dark:bg-gray-700 text-yellow-600 shadow-sm'
                    : 'text-gray-600 dark:text-gray-400'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setPeriod('year')}
                className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${
                  period === 'year'
                    ? 'bg-white dark:bg-gray-700 text-yellow-600 shadow-sm'
                    : 'text-gray-600 dark:text-gray-400'
                }`}
              >
                Yearly
              </button>
            </div>
            
            {/* Hall of Fame Link */}
            <Button
              onClick={() => navigate('/hall-of-fame')}
              variant="outline"
              className="rounded-full border-yellow-400 text-yellow-600 hover:bg-yellow-50"
            >
              <Crown className="w-4 h-4 mr-2" />
              Hall of Fame
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 pb-24">
        {/* Main Tabs */}
        <div className="flex items-center gap-2 mb-6 bg-gray-100 dark:bg-gray-800 rounded-full p-1 w-fit">
          <button
            onClick={() => setActiveTab('awards')}
            className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-bold transition-all ${
              activeTab === 'awards' ? 'bg-white dark:bg-gray-700 text-yellow-600 shadow-md' : 'text-gray-500 hover:text-gray-700'
            }`}
            data-testid="awards-tab"
          >
            <Trophy className="w-4 h-4" /> Awards
          </button>
          <button
            onClick={() => setActiveTab('roastgallery')}
            className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-bold transition-all ${
              activeTab === 'roastgallery' ? 'bg-gradient-to-r from-red-500 to-orange-500 text-white shadow-md' : 'text-gray-500 hover:text-gray-700'
            }`}
            data-testid="roastgallery-tab"
          >
            <Flame className="w-4 h-4" /> #RoastGallery
          </button>
          <button
            onClick={() => setActiveTab('memegallery')}
            className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-bold transition-all ${
              activeTab === 'memegallery' ? 'bg-gradient-to-r from-green-400 to-emerald-500 text-white shadow-md' : 'text-gray-500 hover:text-gray-700'
            }`}
            data-testid="memegallery-tab"
          >
            <SmilePlus className="w-4 h-4" /> #MemeGallery
          </button>
          <button
            onClick={() => setActiveTab('hallofshame')}
            className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-bold transition-all ${
              activeTab === 'hallofshame' ? 'bg-gradient-to-r from-gray-700 to-gray-900 text-white shadow-md' : 'text-gray-500 hover:text-gray-700'
            }`}
            data-testid="hallofshame-tab"
          >
            <Skull className="w-4 h-4" /> #HallofShame
          </button>
          <button
            onClick={() => setActiveTab('bsaward')}
            className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-bold transition-all ${
              activeTab === 'bsaward' ? 'bg-gradient-to-r from-amber-500 to-yellow-600 text-white shadow-md' : 'text-gray-500 hover:text-gray-700'
            }`}
            data-testid="bsaward-tab"
          >
            <AlertTriangle className="w-4 h-4" /> #BSAward
          </button>
        </div>

        {activeTab === 'awards' && (
          <div>
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="p-4 bg-white dark:bg-gray-800 rounded-2xl">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 rounded-xl">
                  <Trophy className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.monthly_votes}</p>
                  <p className="text-sm text-gray-500">Votes This Month</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 bg-white dark:bg-gray-800 rounded-2xl">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-xl">
                  <BarChart3 className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.yearly_votes}</p>
                  <p className="text-sm text-gray-500">Votes This Year</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 bg-white dark:bg-gray-800 rounded-2xl">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-xl">
                  <Users className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.unique_voters}</p>
                  <p className="text-sm text-gray-500">Voters This Month</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 bg-white dark:bg-gray-800 rounded-2xl">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-xl">
                  <Award className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.categories}</p>
                  <p className="text-sm text-gray-500">Award Categories</p>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Award Categories */}
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Award className="w-5 h-5 text-yellow-500" />
          Award Categories
        </h2>
        
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          {categories.map((category) => {
            const config = CATEGORY_CONFIG[category.id];
            const Icon = config?.icon || Award;
            const voteCount = stats?.category_stats?.[category.id] || 0;
            
            return (
              <motion.button
                key={category.id}
                onClick={() => fetchCategoryLeaderboard(category.id)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="p-4 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 hover:border-yellow-400 dark:hover:border-yellow-600 transition-colors text-center"
              >
                <div
                  className="w-12 h-12 rounded-xl mx-auto mb-2 flex items-center justify-center"
                  style={{ backgroundColor: `${config?.color}20` }}
                >
                  <span className="text-2xl">{category.emoji}</span>
                </div>
                <h3 className="font-semibold text-sm text-gray-900 dark:text-white mb-1">
                  {category.name.replace(' Award', '')}
                </h3>
                <p className="text-xs text-gray-500">{voteCount} votes</p>
              </motion.button>
            );
          })}
        </div>

        {/* Leaderboards Grid */}
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-yellow-500" />
          {period === 'month' ? 'Monthly' : 'Yearly'} Leaderboards
        </h2>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="h-64 bg-gray-200 dark:bg-gray-700 rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(leaderboards).map(([categoryId, data]) => {
              const config = CATEGORY_CONFIG[categoryId];
              const Icon = config?.icon || Award;
              
              return (
                <Card 
                  key={categoryId} 
                  className="p-4 bg-white dark:bg-gray-800 rounded-2xl cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => fetchCategoryLeaderboard(categoryId)}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: `${config?.color}20` }}
                    >
                      <Icon className="w-5 h-5" style={{ color: config?.color }} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-gray-900 dark:text-white text-sm">
                        {data.category_info?.name}
                      </h3>
                      <p className="text-xs text-gray-500">{config?.emoji} {period === 'month' ? 'Monthly' : 'Yearly'}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </div>
                  
                  {data.leaderboard?.length > 0 ? (
                    <div className="space-y-2">
                      {data.leaderboard.slice(0, 3).map((entry, idx) => (
                        <div key={entry.content_id} className="flex items-center gap-3">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                            idx === 0 ? 'bg-yellow-400 text-white' :
                            idx === 1 ? 'bg-gray-300 text-gray-700' :
                            'bg-amber-600 text-white'
                          }`}>
                            {idx + 1}
                          </div>
                          <Avatar className="w-8 h-8">
                            <AvatarImage src={entry.owner?.avatar} />
                            <AvatarFallback>{entry.owner?.name?.charAt(0)}</AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                              {entry.owner?.name || entry.owner?.username}
                            </p>
                            <p className="text-xs text-gray-500">{entry.vote_count} votes</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-gray-500 text-sm">
                      No votes yet
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}

        {/* Past Winners */}
        {winners.length > 0 && (
          <>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mt-8 mb-4 flex items-center gap-2">
              <Crown className="w-5 h-5 text-yellow-500" />
              Past Winners
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {winners.map((winner) => {
                const config = CATEGORY_CONFIG[winner.category];
                
                return (
                  <Card key={winner.winner_id} className="p-4 bg-gradient-to-br from-yellow-50 to-amber-50 dark:from-gray-800 dark:to-yellow-900/20 rounded-2xl border border-yellow-200 dark:border-yellow-800">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center">
                        <Trophy className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900 dark:text-white">
                          {winner.category_info?.name}
                        </h3>
                        <p className="text-xs text-gray-500">
                          {winner.month || winner.year} • {winner.vote_count} votes
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3 p-3 bg-white dark:bg-gray-800 rounded-xl">
                      <Avatar className="w-10 h-10">
                        <AvatarImage src={winner.owner?.avatar} />
                        <AvatarFallback>{winner.owner?.name?.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-semibold text-gray-900 dark:text-white">
                          {winner.owner?.name}
                        </p>
                        <p className="text-sm text-gray-500">@{winner.owner?.username}</p>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </>
        )}
        </div>
        )}

      {/* Category Detail Modal */}
      {selectedCategory && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={() => setSelectedCategory(null)}
        >
          <motion.div
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white dark:bg-gray-800 rounded-3xl w-full max-w-lg max-h-[80vh] overflow-y-auto p-6"
          >
            <div className="flex items-center gap-3 mb-6">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${selectedCategory.config?.color}20` }}
              >
                <span className="text-2xl">{selectedCategory.category_info?.emoji}</span>
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  {selectedCategory.category_info?.name}
                </h2>
                <p className="text-sm text-gray-500">{period === 'month' ? 'Monthly' : 'Yearly'} Leaderboard</p>
              </div>
            </div>

            {selectedCategory.leaderboard?.length > 0 ? (
              <div className="space-y-3">
                {selectedCategory.leaderboard.map((entry, idx) => (
                  <div 
                    key={entry.content_id} 
                    className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl"
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                      idx === 0 ? 'bg-yellow-400 text-white' :
                      idx === 1 ? 'bg-gray-300 text-gray-700' :
                      idx === 2 ? 'bg-amber-600 text-white' :
                      'bg-gray-200 text-gray-600'
                    }`}>
                      {idx === 0 ? <Crown className="w-4 h-4" /> : idx + 1}
                    </div>
                    <Avatar className="w-10 h-10">
                      <AvatarImage src={entry.owner?.avatar} />
                      <AvatarFallback>{entry.owner?.name?.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900 dark:text-white">
                        {entry.owner?.name || entry.owner?.username}
                      </p>
                      <p className="text-xs text-gray-500">@{entry.owner?.username}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-yellow-600">{entry.vote_count}</p>
                      <p className="text-xs text-gray-500">votes</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No votes yet in this category
              </div>
            )}

            <Button
              onClick={() => setSelectedCategory(null)}
              className="w-full mt-6 rounded-full"
              variant="outline"
            >
              Close
            </Button>
          </motion.div>
        </motion.div>
      )}

      {activeTab === 'roastgallery' && (
        <RoastGalleryTab
          topRoaster={topRoaster} roastLeaderboard={roastLeaderboard} roastFeed={roastFeed}
          roastForm={roastForm} setRoastForm={setRoastForm} submittingRoast={submittingRoast}
          submitRoast={submitRoast} fireRoast={fireRoast} period={period}
        />
      )}

      {activeTab === 'memegallery' && (
        <MemeGalleryTab
          topMemer={topMemer} memeLeaderboard={memeLeaderboard} memeFeed={memeFeed}
          memeOfTheDay={memeOfTheDay} memeSort={memeSort} setMemeSort={setMemeSort}
          laughAtMeme={laughAtMeme} renderMedia={renderMedia} period={period}
        />
      )}

      {activeTab === 'hallofshame' && (
        <HallOfShameTab
          topShamer={topShamer} shameLeaderboard={shameLeaderboard} shameFeed={shameFeed}
          shameSort={shameSort} setShameSort={setShameSort} shamePost={shamePost}
          renderMedia={renderMedia} period={period}
        />
      )}

      {activeTab === 'bsaward' && (
        <BSAwardTab
          topBsCaller={topBsCaller} bsLeaderboard={bsLeaderboard} bsFeed={bsFeed}
          bsSort={bsSort} setBsSort={setBsSort} callBs={callBs}
          renderMedia={renderMedia} period={period}
        />
      )}
      </div>
      <FloatingDock user={user} />
    </div>
  );
};

export default Awards;
